/**
 * AppDataGrid — the ONE shared data grid for every form in Alpaca Intelligence
 * (CLAUDE.md §18.6 / §7.5).  Forms pass only `rows`, `columns` and a little
 * config; the grid owns the styling, sorting, per-column filtering, the footer
 * (Part C) and the load-time indicator.  No form defines its own grid styling
 * or re-implements pagination — that is the whole point of standardising here.
 *
 * Enforced identically everywhere (see appGrid.css):
 *   • One visual style + one font size.
 *   • Vertical column separators on every row (not just the header).
 *   • Cell text BLACK by default; ONLY a `type:'status'` column is coloured
 *     (via the shared statusPill mapping).  IDs and every other field are black.
 *   • Light sort header (normal weight, lighter caret — never bolds).
 *   • Light-orange row hover (never blue).  Colourful filter icon.
 *   • Date / datetime columns render through the shared fmtLocal* helpers
 *     (§14b.6) — local timezone, 24-hour, no AM/PM, no inline date formatting.
 *
 * Performance: only the current page's rows are rendered to the DOM, so a
 * 10k-row dataset stays snappy (filter / sort run on the in-memory array).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { useSort } from '../SortableHeader';
import { statusVariant } from '../statusPill';
import { ColFilterButton, passesColumnFilter, cascadeRows, type ColumnFilterValue } from '../ColumnFilterDropdown';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { LoaderSymbol } from '../loading';
import { InlineCellEditor } from '../gridEdit';
import { HeaderContextMenu } from './HeaderContextMenu';
import { ColumnChooser } from './ColumnChooser';
import { loadGridPrefs, saveGridPrefs } from './gridPrefs';
import './appGrid.css';

/** Column data type — drives default formatting, alignment and filtering. */
export type AppColType = 'text' | 'number' | 'date' | 'datetime' | 'status';

export interface AppColumn<T> {
  /** Stable key — also the default property read from the row. */
  field: string;
  /** Header caption — may be a ReactNode for styled labels (e.g. red asterisk).
   *  When ReactNode is used, provide `headerText` (plain string) for export/filter. */
  header: string | ReactNode;
  /** Plain-text label used for CSV/Excel export headers and column-filter popup
   *  when `header` is a ReactNode.  Falls back to `header` when it is a string. */
  headerText?: string;
  /** Data type (default 'text').  'date'/'datetime' format via fmtLocal*. */
  type?: AppColType;
  width?: number | string;
  minWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  /** Wrap long cell text onto multiple lines instead of ellipsis-clipping it on
   *  one line (e.g. a free-text column like Sampling Equipment). Opt-in per
   *  column; pairs well with `fixedLayout` so one long value can't blow the
   *  column — and the whole table — wider than its container. */
  wrap?: boolean;
  /** Default true.  Set false for action/custom columns. */
  sortable?: boolean;
  /** Default true (false when no header).  Filters on the displayed text. */
  filterable?: boolean;
  /** Set true to exclude this column from the shared Export (gridExportMatrix). */
  noExport?: boolean;
  /** Raw value override (defaults to `row[field]`). */
  value?: (row: T) => unknown;
  /** Custom cell renderer (e.g. progress bar, link).  Cell text stays black. */
  render?: (row: T) => ReactNode;
  /** Override the text used for filtering + sorting (defaults to the display text). */
  text?: (row: T) => string;
  /** Custom comparator for this column's sort (overrides the default value/text
   *  comparison). Takes two rows, returns <0 / 0 / >0. */
  compare?: (a: T, b: T) => number;
  /** Right-click handler on the &lt;td&gt;.  Fires regardless of whether the column
   *  uses `edit` or `render` — attach context-menu logic here so it is not
   *  swallowed by the InlineCellEditor branch. */
  onCellContextMenu?: (e: ReactMouseEvent<HTMLTableCellElement>, row: T) => void;
  /** Opt-in INLINE EDITING (BO UI Standard §9 "pure grid, in-place editing").
   *  When set AND the grid is given `onCellCommit`, the cell becomes
   *  click-to-edit via the shared <InlineCellEditor> — no editor is drawn at
   *  rest; it opens on click, commits on blur/Enter, reverts on Escape. Existing
   *  grids that don't set `edit` are completely unaffected. */
  edit?: {
    type?: 'text' | 'combo' | 'datetime' | 'date';
    /** Options for the 'combo' editor — a static list or per-row. */
    options?: string[] | ((row: T) => string[]);
    trigger?: 'click' | 'double-click';
    /** Per-row read-only (e.g. a locked row) — renders text, never opens. */
    disabled?: (row: T) => boolean;
    /** Per-row required-yet-empty yellow tint (§9). */
    requiredEmpty?: (row: T) => boolean;
  };
}

export interface AppDataGridProps<T> {
  rows: T[];
  columns: AppColumn<T>[];
  /** Stable row id (defaults to the row index). */
  getRowId?: (row: T, index: number) => string | number;
  loading?: boolean;
  /** @deprecated The load-time indicator now renders ONLY at the TOP of the
   *  form via the shared <FormTitle> chip (CLAUDE.md §18.6 — one location, top).
   *  Still accepted so existing callers compile, but the grid ignores it. */
  loadMs?: number | null;
  /** Default rows-per-page (default 20). */
  defaultPageSize?: number;
  /** Page-size dropdown options (default 10 / 20 / 50 / 100). */
  pageSizeOptions?: number[];
  /** Show a Maximize/Restore control on the grid's own header bar (ALPACA
   *  dashboard-grid parity). When on, a small header strip renders above the
   *  grid with an optional `gridTitle` on the left and a maximize icon on the
   *  right; clicking it fullscreens the grid (position:fixed), Esc/the icon
   *  restores. Off by default so existing grids are unchanged. */
  maximizable?: boolean;
  /** Optional caption shown on the left of the maximizable header strip (e.g.
   *  "Result Approval"). Only rendered when `maximizable` is true. */
  gridTitle?: string;
  onRowClick?: (row: T) => void;
  /** Fires on a row double-click (e.g. open the record for edit). */
  onRowDoubleClick?: (row: T) => void;
  selectedRowId?: string | number | null;
  /** Show a leading checkbox column for multi-row selection (requires getRowId). */
  selectable?: boolean;
  /** Controlled set of selected row ids (used with `selectable`). */
  selectedIds?: Set<string | number>;
  /** Called with the next full selection Set whenever it changes. */
  onSelectionChange?: (ids: Set<string | number>) => void;
  /** When `selectable` is on, suppress the checkbox column entirely and let a
   *  row CLICK toggle its own selection instead (still drives `selectedIds` /
   *  `onSelectionChange` and the `adg-selected` row highlight) — for designs
   *  that select-to-act without a visible checkbox (e.g. a prototype's
   *  click-a-row rollback picker). Only meaningful together with `selectable`. */
  hideCheckboxColumn?: boolean;
  /** Freeze (pin) the first N DATA columns so they stay visible while the rest
   *  of the grid scrolls horizontally (BO UI Standard — "sticky first column",
   *  §7.1). The leading checkbox column (when `selectable`) is pinned too. Each
   *  frozen column MUST carry a numeric `width` so the left offsets compute
   *  correctly. Default 0 = no frozen columns (existing grids unaffected). */
  stickyColumns?: number;
  /** Inline-edit commit (§9). Required for any column with `edit` to be
   *  editable — called with the row, the column, and the committed string. */
  onCellCommit?: (row: T, column: AppColumn<T>, newValue: string) => void;
  /** Optional: fires on every keystroke WHILE a text/combo cell is still being
   *  edited (does not close the cell) — wire this into a batch-edit buffer so
   *  "dirty" state (and anything gated on it, e.g. a Save button) updates the
   *  moment the user types, without depending on blur/Enter firing first.
   *  `onCellCommit` still fires as usual when the edit finishes. */
  onCellChange?: (row: T, column: AppColumn<T>, newValue: string) => void;
  /** Fires with the current filtered + sorted row set (pre-pagination) whenever
   *  it changes — i.e. after the grid's OWN per-column header filters are
   *  applied, not just the caller's `rows`.  Wire this into the form's
   *  `AppToolbar exportData` (via `gridExportMatrix`) so "Export to" exports
   *  exactly what the grid is showing, including any active column filter —
   *  otherwise an export silently includes rows a column filter hid. */
  onVisibleRowsChange?: (rows: T[]) => void;
  /** Show the per-column filter row (default true). */
  showFilters?: boolean;
  emptyText?: string;
  loadingText?: string;
  className?: string;
  /** Initial sort column + direction, e.g. { key: 'name', dir: 'asc' }. */
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Opt-in per-row CSS class (e.g. a domain "new / modified" indicator that
   *  mirrors a legacy ConditionalAppearance rule, like MOR Adhoc's Bench QC or
   *  MSR's ALPACA-parity green "newly retrieved" row). Leave unset for the
   *  default grid look — CLAUDE.md §18.6 still applies: only a `type:'status'`
   *  column colours by default; this exists for the rare case where a whole
   *  ROW (not a column) must carry legacy's row-level semantic colour. */
  getRowClassName?: (row: T) => string | undefined;
  /** When set, this grid's column visibility / order / widths / sort persist to
   *  localStorage under this key and restore on the next visit. Omit for the
   *  default in-memory-only behaviour. Must be UNIQUE per grid (e.g. a form id). */
  persistKey?: string;
  /** Use `table-layout: fixed` so the table is ALWAYS exactly its container width:
   *  columns keep their configured widths (proportionally), a long cell can never
   *  push the table wider than its container, and there is no horizontal scroll.
   *  Pairs with per-column `wrap` for a free-text column. Opt-in (default auto). */
  fixedLayout?: boolean;
  /** Let header labels WRAP onto multiple lines instead of ellipsis-clipping on
   *  one line. Useful in a width-constrained container (e.g. a popup clamped by
   *  the viewport) so multi-word headers ("Sample Matrix", "Sampling Equipment")
   *  stay fully readable in a narrow column. Opt-in (default single-line). */
  wrapHeaders?: boolean;
}

// 100 = the app-wide default page size (user decision 2026-07-02, GoLive
// parity — matches components/listDefaults.ts DEFAULT_PAGE_SIZE).
const DEFAULT_PAGE_SIZE = 100;
// App-wide page-size dropdown, GoLive parity (matches the Client-picker set the
// user referenced 2026-08-03). 10 is kept because several small grids default to
// it; the high sizes (200/500/1000/2000) mirror GoLive's lookup popups. This is
// the single source — forms no longer pass their own pageSizeOptions.
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100, 200, 500, 1000, 2000];

function rawValue<T>(row: T, col: AppColumn<T>): unknown {
  if (col.value) return col.value(row);
  return (row as Record<string, unknown>)[col.field];
}

/** The text shown in the cell (also used for filtering + text/status sorting). */
function displayText<T>(row: T, col: AppColumn<T>): string {
  if (col.text) return col.text(row);
  const v = rawValue(row, col);
  if (v === null || v === undefined) return '';
  switch (col.type) {
    case 'date':
      return fmtLocalDate(v as string);
    case 'datetime':
      return fmtLocalDateTime(v as string);
    default:
      return String(v);
  }
}

/**
 * Build the export matrix (headers + display-formatted string cells) for a set
 * of rows against the grid's columns — the ONE shared way to feed <ExportMenu>
 * / <AppToolbar exportData>.  Uses the SAME displayText as the grid cells, so the
 * export matches what's on screen (dates via fmtLocal*, col.text/value honoured).
 * Pass the form's FULL filtered dataset (not the visible page) to export all rows.
 * A column can opt out of export with `noExport: true` (e.g. a checkbox column).
 */
export function gridExportMatrix<T>(rows: readonly T[], columns: AppColumn<T>[]): { headers: string[]; rows: string[][] } {
  const cols = columns.filter((c) => !c.noExport);
  return {
    headers: cols.map((c) => c.headerText ?? (typeof c.header === 'string' ? c.header : '')),
    rows: rows.map((r) => cols.map((c) => displayText(r, c))),
  };
}

/** Value used for sorting — raw for number/date so the comparator stays smart. */
function sortValue<T>(row: T, col: AppColumn<T>): unknown {
  if (col.type === 'number' || col.type === 'date' || col.type === 'datetime') {
    return rawValue(row, col);
  }
  return displayText(row, col);
}

/** Build the page-button list with leading/trailing ellipsis windows. */
function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('gap');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('gap');
  out.push(total);
  return out;
}

export function AppDataGrid<T>(props: AppDataGridProps<T>) {
  const {
    rows,
    columns,
    getRowId,
    loading = false,
    // `loadMs` is intentionally NOT consumed here: the load-time indicator now
    // lives ONLY at the TOP of the form (shared <FormTitle> chip), never in the
    // grid footer (CLAUDE.md §18.6 — one location, top).  The prop is kept on
    // the interface for source-compat with callers that still pass it.
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    maximizable = false,
    gridTitle,
    onRowClick,
    onRowDoubleClick,
    selectedRowId = null,
    selectable = false,
    selectedIds,
    onSelectionChange,
    hideCheckboxColumn = false,
    stickyColumns = 0,
    onCellCommit,
    onCellChange,
    showFilters = true,
    emptyText = 'No records to display',
    loadingText = 'Loading…',
    className,
    onVisibleRowsChange,
    initialSort,
    getRowClassName,
    persistKey,
    fixedLayout = false,
    wrapHeaders = false,
  } = props;

  // Saved per-grid prefs (column layout + sort), loaded once. Empty when no
  // persistKey or nothing saved yet — so unpersisted grids behave exactly as before.
  const savedPrefs = useMemo(() => loadGridPrefs(persistKey), [persistKey]);
  const sorter = useSort<T>(
    savedPrefs.sort?.key ?? initialSort?.key ?? null,
    savedPrefs.sort?.dir ?? initialSort?.dir ?? null,
  );
  // Default filter-list direction (CLAUDE.md §18.6, 2026-06-18): when a column
  // is NOT actively click-sorted, its header-filter still lists values in the
  // grid's natural order — DESC when the grid's first column is an ID (records
  // are shown newest-first by that ID), otherwise ASC.  Header "…ID" (e.g.
  // JobID) or field "…Id" (jobId) marks an ID column.  An actively click-sorted
  // column overrides this with its own direction (see the ColFilterButton below).
  const defaultFilterDir: 'asc' | 'desc' = useMemo(() => {
    const first = columns[0];
    const firstHeader = typeof first?.header === 'string' ? first.header : (first?.headerText ?? '');
    const isId = !!first && (/ID$/.test(firstHeader) || /Id$/.test(first.field ?? ''));
    return isId ? 'desc' : 'asc';
  }, [columns]);
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Maximize/Restore (grid-control feature, ALPACA parity) — fullscreens the
  // grid root; Esc restores. Only active when the caller opts in via `maximizable`.
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);
  // User-dragged column widths (override col.width).  Every column is resizable.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => savedPrefs.widths ?? {});
  // True while a resize drag is in progress. The header <th> is ALSO draggable
  // (column reorder), so onDragStart checks this to NOT start a reorder when the
  // user grabbed the resize handle.
  const resizingRef = useRef(false);
  const startResize = (field: string, e: ReactMouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const startX = e.clientX;
    const startW = th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) =>
      setColWidths((prev) => ({ ...prev, [field]: Math.max(60, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Clear on the next tick so a dragstart that fires right after mouseup
      // still sees the resize flag and cancels the reorder.
      setTimeout(() => { resizingRef.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ── Column management: hide / show-all / chooser / drag-reorder ─────────────
  // Hidden columns (by field) + a user column order, both in-memory per grid
  // instance (like colWidths above). Every column can be resized (handle),
  // reordered (drag a header), hidden (header menu / chooser) and restored.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(savedPrefs.hidden ?? []));
  const [order, setOrder] = useState<string[] | null>(() => savedPrefs.order ?? null);
  // Column Chooser popup anchor (viewport coords) or null when closed.
  const [chooser, setChooser] = useState<{ x: number; y: number } | null>(null);
  // Persist column layout + sort whenever any of them change (best-effort; no-op
  // without a persistKey).
  useEffect(() => {
    saveGridPrefs(persistKey, {
      hidden: [...hidden],
      order: order ?? undefined,
      widths: colWidths,
      sort: { key: sorter.sort.key, dir: sorter.sort.dir },
    });
  }, [persistKey, hidden, order, colWidths, sorter.sort]);
  // Header being dragged / dragged-over, for the reorder drop indicator.
  const [dragField, setDragField] = useState<string | null>(null);
  const [dragOverField, setDragOverField] = useState<string | null>(null);

  // Header right-click context menu (Sort · … more features land here one at a
  // time): the field of the right-clicked column + the cursor position, or null
  // when closed.
  const [headerMenu, setHeaderMenu] = useState<{ field: string; x: number; y: number } | null>(null);

  const colByField = useMemo(() => {
    const m = new Map<string, AppColumn<T>>();
    for (const c of columns) m.set(c.field, c);
    return m;
  }, [columns]);

  /** Plain-text header label for a column (menu title, export, filter). */
  const headerLabel = (col: AppColumn<T>): string =>
    col.headerText ?? (typeof col.header === 'string' ? col.header : col.field);

  // Columns in the user's chosen order (unknown / newly-added fields keep their
  // natural position at the end), then filtered to the non-hidden set — this is
  // the list the grid actually renders (header + body + sticky + colCount).
  const orderedColumns = useMemo(() => {
    if (!order) return columns;
    const idx = new Map(order.map((f, i) => [f, i] as const));
    return [...columns].sort((a, b) => (idx.get(a.field) ?? 1e9) - (idx.get(b.field) ?? 1e9));
  }, [columns, order]);
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hidden.has(c.field)),
    [orderedColumns, hidden],
  );

  // A grid always shows ≥1 column — the "keep one visible" guard is computed from
  // `prev` inside each setter so it never trips on a stale closure.
  const visibleCountAfter = (prev: Set<string>) =>
    orderedColumns.filter((c) => !prev.has(c.field)).length;
  const hideColumn = (field: string) =>
    setHidden((prev) => {
      if (visibleCountAfter(prev) <= 1) return prev;
      const next = new Set(prev); next.add(field); return next;
    });
  const showAllColumns = () => setHidden(new Set());
  const toggleColumn = (field: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else if (visibleCountAfter(prev) > 1) next.add(field);
      return next;
    });
  const reorderColumns = (from: string, to: string) => {
    if (!from || from === to) return;
    setOrder(() => {
      const cur = orderedColumns.map((c) => c.field);
      const fi = cur.indexOf(from);
      if (fi < 0) return cur;
      cur.splice(fi, 1);
      const ti = cur.indexOf(to);
      cur.splice(ti < 0 ? cur.length : ti, 0, from);   // drop BEFORE the target
      return cur;
    });
  };

  // Excel-style cascade: a column's filter dropdown lists only the values that
  // pass the OTHER active column filters (the edited column is excluded), so
  // picking one column narrows the rest.  `cascadeRows` returns `rows` unchanged
  // (no copy) when no other filter is active, so this is free in the common case.
  const cascadeText = (row: T, key: string): string => {
    const c = colByField.get(key);
    return c ? displayText(row, c) : '';
  };

  // Filter → sort → (paginate happens after).  Per-column filtering uses the
  // shared Excel-style multi-select dropdown (ColumnFilterDropdown), matching on
  // the displayed text so a date column filters by exactly what the cell shows
  // (§14b.6).  Same UX as every legacy grid.
  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v !== null);
    if (active.length === 0) return rows;
    return rows.filter((row) =>
      active.every(([field, v]) => {
        const col = colByField.get(field);
        if (!col) return true;
        return passesColumnFilter(v, displayText(row, col));
      }),
    );
  }, [rows, filters, colByField]);

  const sorted = useMemo(
    () => {
      // A column may supply its own comparator (compare); otherwise fall back to
      // the default smart value/text sort.
      const { key, dir } = sorter.sort;
      const col = key ? colByField.get(key) : undefined;
      if (key && dir && col?.compare) {
        const mult = dir === 'asc' ? 1 : -1;
        return filtered.slice().sort((a, b) => mult * col.compare!(a, b));
      }
      return sorter.apply(filtered, (row, k) => {
        const c = colByField.get(k);
        return c ? sortValue(row, c) : (row as Record<string, unknown>)[k];
      });
    },
    [filtered, sorter, colByField],
  );

  // Report the live filtered+sorted set (pre-pagination) so a caller's export
  // (AppToolbar exportData via gridExportMatrix) reflects the grid's OWN
  // column filters, not just whatever `rows` the caller happened to pass in.
  // `onVisibleRowsChange` intentionally excluded from deps — an inline arrow
  // function identity must not retrigger this on every render.
  useEffect(() => {
    onVisibleRowsChange?.(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  // Snap back to page 1 whenever filters or page size change.
  useEffect(() => setPage(1), [filters, pageSize]);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  const setColFilter = (field: string, v: ColumnFilterValue) =>
    setFilters((prev) => ({ ...prev, [field]: v }));

  // ── Optional multi-row selection (checkbox column) ──
  const rowKey = (row: T, i: number): string | number => (getRowId ? getRowId(row, i) : i);
  // Select-all toggles every FILTERED row (across pages), the usual bulk-action scope.
  const allIds = useMemo(
    () => (selectable ? sorted.map((r, i) => rowKey(r, i)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectable, sorted, getRowId],
  );
  const allChecked = selectable && allIds.length > 0 && !!selectedIds && allIds.every((id) => selectedIds.has(id));
  const someChecked = selectable && !!selectedIds && allIds.some((id) => selectedIds.has(id));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds ?? []);
    // If ANYTHING in the current (filtered) set is selected, a header click
    // CLEARS it — a reliable "uncheck all".  Only when nothing is selected does
    // it select all.  (Previously it cleared only when EVERY row was selected,
    // so grids that cap selection via a guard — e.g. Custom Reporting's
    // same-client rule — could never reach allChecked and thus could never
    // uncheck from the header.  2026-06-28.)
    if (allChecked || someChecked) allIds.forEach((id) => next.delete(id));
    else allIds.forEach((id) => next.add(id));
    onSelectionChange(next);
  };
  const toggleOne = (id: string | number) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds ?? []);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const showCheckboxColumn = selectable && !hideCheckboxColumn;
  const colCount = visibleColumns.length + (showCheckboxColumn ? 1 : 0);

  // ── Frozen (pinned) leading columns (opt-in via `stickyColumns`) ──────────
  // The pinned DATA column's left offset = the checkbox column width (when
  // selectable) + the widths of every pinned column before it.  Because the grid
  // uses AUTO table-layout, a column's RENDERED width can differ from its
  // configured `width`, so summing config alone drifts and the frozen columns
  // overlap the scrolling ones.  We MEASURE the real header-cell widths (layout
  // effect below) and stack those; the configured widths are a first-paint
  // fallback only.
  const CB_STICKY_WIDTH = 34;   // matches .adg-cb-col
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const [measuredLefts, setMeasuredLefts] = useState<number[]>([]);
  const stickyWidthOf = (col: AppColumn<T> | undefined): number => {
    if (!col) return 140;
    const w = colWidths[col.field] ?? col.width;
    return typeof w === 'number' ? w : 140;
  };
  const stickyLeftOf = (index: number): number | undefined => {
    if (index >= stickyColumns) return undefined;
    if (measuredLefts.length > index) return measuredLefts[index]!;   // measured — accurate
    let left = showCheckboxColumn ? CB_STICKY_WIDTH : 0;              // first-paint fallback
    for (let k = 0; k < index; k++) left += stickyWidthOf(visibleColumns[k]);
    return left;
  };
  const cbSticky = stickyColumns > 0;

  // Measure the real pinned-column left offsets from the rendered header cells
  // (getBoundingClientRect().width is unaffected by the sticky position, so it
  // reflects the true column width).  Re-runs when the columns, data, page or a
  // manual resize could have changed a width — and on window resize.
  useLayoutEffect(() => {
    if (stickyColumns <= 0) { setMeasuredLefts((prev) => (prev.length ? [] : prev)); return; }
    const rowEl = headRowRef.current;
    if (!rowEl) return;
    const measure = () => {
      const cells = Array.from(rowEl.children) as HTMLElement[];
      const dataStart = showCheckboxColumn ? 1 : 0;
      const lefts: number[] = [];
      let acc = showCheckboxColumn ? (cells[0]?.getBoundingClientRect().width ?? 0) : 0;
      for (let i = 0; i < stickyColumns; i++) {
        lefts[i] = acc;
        acc += cells[dataStart + i]?.getBoundingClientRect().width ?? 0;
      }
      setMeasuredLefts((prev) =>
        prev.length === lefts.length && prev.every((v, i) => Math.abs(v - lefts[i]!) < 0.5)
          ? prev : lefts);
    };
    measure();
    // Re-measure on ANY size change (auto table-layout can resize a column after
    // paint — fonts, async content, container resize — which the deps below don't
    // catch).  Observe each header cell so an individual column widening re-stacks
    // the pinned offsets immediately (fixes frozen/scrolled column overlap).
    const ro = new ResizeObserver(measure);
    ro.observe(rowEl);
    for (const c of Array.from(rowEl.children)) ro.observe(c as Element);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [stickyColumns, showCheckboxColumn, visibleColumns, colWidths, pageRows, safePage, pageSize]);

  return (
    <div className={`adg${className ? ` ${className}` : ''}${maximized ? ' adg-maximized' : ''}`}>
      {maximizable && (
        <div className="adg-header">
          {gridTitle && <span className="adg-header-title">{gridTitle}</span>}
          <span className="adg-header-sp" />
          <button
            type="button"
            className="adg-max-btn"
            onClick={() => setMaximized(m => !m)}
            title={maximized ? 'Restore (Esc)' : 'Maximize'}
            aria-label={maximized ? 'Restore' : 'Maximize'}
          >
            <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="adg-scroll">
        <table className={`adg-table${fixedLayout ? ' adg-fixed' : ''}${wrapHeaders ? ' adg-wrap-headers' : ''}`}>
          <thead>
            <tr ref={headRowRef}>
              {showCheckboxColumn && (
                <th
                  className={`adg-cb-col${cbSticky ? ' adg-sticky' : ''}`}
                  style={cbSticky ? { left: 0 } : undefined}
                >
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((col, ci) => {
                const sortable = col.sortable !== false;
                const filterable = showFilters && col.filterable !== false && !!col.header;
                const isSorted = sorter.sort.key === col.field && sorter.sort.dir !== null;
                const caret = !isSorted ? '⇅' : sorter.sort.dir === 'asc' ? '▲' : '▼';
                const stickyLeft = stickyLeftOf(ci);
                const isSticky = stickyLeft !== undefined;
                const isStickyEdge = isSticky && ci === stickyColumns - 1;
                const dragging = dragField === col.field;
                const dropTarget = dragOverField === col.field && dragField !== null && dragField !== col.field;
                return (
                  <th
                    key={col.field}
                    className={`adg-th${sortable ? ' adg-th-sortable' : ''}${col.type === 'number' ? ' adg-th-num' : ''}${isSticky ? ' adg-sticky' : ''}${isStickyEdge ? ' adg-sticky-edge' : ''}${dragging ? ' adg-th-dragging' : ''}${dropTarget ? ' adg-th-dropbefore' : ''}`}
                    // A user-dragged width must also pin minWidth (else the width:100%
                    // AUTO-layout table just squeezes the column back to content width and
                    // the resize is invisible — the ModuleListView grids set both, which is
                    // why THEY resize and this one didn't until now, 2026-08-05).
                    style={{ width: colWidths[col.field] ?? col.width, minWidth: colWidths[col.field] ?? col.minWidth, ...(isSticky ? { left: stickyLeft } : {}) }}
                    onClick={sortable ? () => sorter.setSort(col.field) : undefined}
                    // Right-click → the shared header context menu (Sort · Hide ·
                    // Show All · Chooser). preventDefault suppresses the native menu.
                    onContextMenu={(e) => { e.preventDefault(); setHeaderMenu({ field: col.field, x: e.clientX, y: e.clientY }); }}
                    // Drag-reorder: dragging a header drops it before the target
                    // column. Guarded by resizingRef so grabbing the resize handle
                    // never starts a reorder.
                    draggable
                    onDragStart={(e) => {
                      if (resizingRef.current) { e.preventDefault(); return; }
                      setDragField(col.field);
                      e.dataTransfer.effectAllowed = 'move';
                      try { e.dataTransfer.setData('text/plain', col.field); } catch { /* older browsers */ }
                    }}
                    onDragOver={(e) => {
                      if (dragField && dragField !== col.field) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverField !== col.field) setDragOverField(col.field);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragField) reorderColumns(dragField, col.field);
                      setDragField(null); setDragOverField(null);
                    }}
                    onDragEnd={() => { setDragField(null); setDragOverField(null); }}
                  >
                    <span className="adg-th-inner">
                      <span className="adg-th-label">{col.header}</span>
                      {sortable && (
                        <span className={`adg-sort${isSorted ? ' active' : ''}`}>{caret}</span>
                      )}
                      {filterable && (
                        /* Excel-style multi-select header filter (funnel/chevron
                           → popup) — same UX as every legacy grid. */
                        <ColFilterButton<T>
                          variant="inline"
                          col={col.field}
                          label={col.headerText ?? (typeof col.header === 'string' ? col.header : undefined)}
                          rows={cascadeRows(rows, filters, col.field, cascadeText)}
                          formatter={(r) => displayText(r, col)}
                          /* Order the filter value list to MATCH the column's
                             sort: the actively click-sorted column uses its own
                             direction; every other column falls back to the
                             grid's default (DESC for an ID-first grid, else ASC).
                             Ordered by the raw value so date/number sort by real
                             value, not string. */
                          sortDir={sorter.sort.key === col.field ? sorter.sort.dir : defaultFilterDir}
                          sortKey={(r) => sortValue(r, col)}
                          value={filters[col.field] ?? null}
                          onChange={(v) => setColFilter(col.field, v)}
                        />
                      )}
                    </span>
                    {/* Drag handle — every column is resizable. */}
                    <span
                      className="adg-resize"
                      onMouseDown={(e) => startResize(col.field, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="adg-state">
                    <LoaderSymbol size={20} /> {loadingText}
                  </div>
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>
                  <div className="adg-state empty">
                    <i className="ti ti-database-off" /> {emptyText}
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => {
                const id = getRowId ? getRowId(row, i) : (safePage - 1) * pageSize + i;
                const isSel =
                  (selectedRowId != null && id === selectedRowId) ||
                  (selectable && !!selectedIds && selectedIds.has(id));
                const rowClickToggles = selectable && hideCheckboxColumn;
                const handleRowClick = rowClickToggles
                  ? () => { toggleOne(id); onRowClick?.(row); }
                  : onRowClick ? () => onRowClick(row) : undefined;
                return (
                  <tr
                    key={id}
                    className={`${isSel ? 'adg-selected' : ''}${handleRowClick || onRowDoubleClick ? ' adg-row-click' : ''}${getRowClassName ? ` ${getRowClassName(row) ?? ''}` : ''}`}
                    onClick={handleRowClick}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                  >
                    {showCheckboxColumn && (
                      <td
                        className={`adg-cb-col${cbSticky ? ' adg-sticky' : ''}`}
                        style={cbSticky ? { left: 0 } : undefined}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedIds && selectedIds.has(id)}
                          onChange={() => toggleOne(id)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {visibleColumns.map((col, ci) => {
                      const align =
                        col.align ?? (col.type === 'number' ? 'right' : undefined);
                      const stickyLeft = stickyLeftOf(ci);
                      const isSticky = stickyLeft !== undefined;
                      const isStickyEdge = isSticky && ci === stickyColumns - 1;
                      const cls =
                        (align === 'right'
                          ? 'adg-td-num'
                          : align === 'center'
                            ? 'adg-td-center'
                            : '') +
                        (isSticky ? ' adg-sticky' : '') +
                        (isStickyEdge ? ' adg-sticky-edge' : '') +
                        (col.wrap ? ' adg-wrap' : '');
                      let content: ReactNode;
                      if (col.edit && onCellCommit) {
                        // Opt-in inline editing (§9) — the shared click-to-edit
                        // cell. No editor drawn at rest; commits via onCellCommit.
                        const ec = col.edit;
                        const raw = col.value ? col.value(row) : (row as Record<string, unknown>)[col.field];
                        const strVal = raw == null ? '' : String(raw);
                        const opts = typeof ec.options === 'function' ? ec.options(row) : ec.options;
                        content = (
                          <InlineCellEditor
                            value={strVal}
                            type={ec.type}
                            options={opts}
                            trigger={ec.trigger}
                            disabled={ec.disabled?.(row)}
                            requiredEmpty={ec.requiredEmpty?.(row)}
                            onCommit={(v) => onCellCommit(row, col, v)}
                            onLiveChange={onCellChange ? (v) => onCellChange(row, col, v) : undefined}
                          />
                        );
                      } else if (col.render) content = col.render(row);
                      else if (col.type === 'status') {
                        const label = displayText(row, col);
                        content = label ? (
                          <span className={`adg-pill ${statusVariant(label)}`}>{label}</span>
                        ) : null;
                      } else content = displayText(row, col);
                      // Global empty-cell rule (user request 2026-07-10): a cell whose
                      // rendered content is just a typographic DASH placeholder ('—' /
                      // '–') shows BLANK — no grid shows a dash for empty data.  Real
                      // values are never a bare em/en-dash, so this is safe app-wide and
                      // spares every column from hand-coding the empty case.
                      if (content === '—' || content === '–') content = '';
                      return (
                        <td
                          key={col.field}
                          className={cls.trim()}
                          style={isSticky ? { left: stickyLeft } : undefined}
                          title={typeof content === 'string' ? content : undefined}
                          onContextMenu={col.onCellContextMenu
                            ? e => { e.preventDefault(); col.onCellContextMenu!(e, row); }
                            : undefined}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Header right-click menu (Sort · Hide · Show All · Column Chooser) ── */}
      {headerMenu && (() => {
        const col = colByField.get(headerMenu.field);
        if (!col) return null;
        return (
          <HeaderContextMenu
            column={{ field: col.field, label: headerLabel(col), sortable: col.sortable !== false }}
            sort={sorter.sort}
            position={{ x: headerMenu.x, y: headerMenu.y }}
            onSort={(dir) => sorter.setSortExplicit(dir === null ? null : col.field, dir)}
            onHideColumn={() => hideColumn(col.field)}
            canHide={visibleColumns.length > 1}
            onShowAllColumns={showAllColumns}
            hasHiddenColumns={hidden.size > 0}
            onColumnChooser={() => setChooser({ x: headerMenu.x, y: headerMenu.y })}
            onClose={() => setHeaderMenu(null)}
          />
        );
      })()}

      {/* ── Column Chooser popup (opened from the header menu) ── */}
      {chooser && (
        <ColumnChooser
          columns={orderedColumns.map((c) => ({ field: c.field, label: headerLabel(c) }))}
          hidden={hidden}
          position={chooser}
          onToggle={toggleColumn}
          onShowAll={showAllColumns}
          onClose={() => setChooser(null)}
        />
      )}

      {/* ── Footer / pagination (Part C) ── */}
      <div className="adg-footer">
        <div className="adg-foot-left">
          <span>
            Page {safePage} of {totalPages} ({total} item{total === 1 ? '' : 's'})
          </span>
        </div>

        <div className="adg-pages">
          <button
            className="adg-pg-btn"
            disabled={safePage <= 1}
            onClick={() => setPage(safePage - 1)}
            aria-label="Previous page"
          >
            <i className="ti ti-chevron-left" />
          </button>
          {pageWindow(safePage, totalPages).map((p, idx) =>
            p === 'gap' ? (
              <span key={`gap-${idx}`} className="adg-pg-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`adg-pg-btn${p === safePage ? ' active' : ''}`}
                onClick={() => setPage(p)}
                aria-label={`Page ${p}`}
                aria-current={p === safePage ? 'page' : undefined}
              >
                {p}
              </button>
            ),
          )}
          <button
            className="adg-pg-btn"
            disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}
            aria-label="Next page"
          >
            <i className="ti ti-chevron-right" />
          </button>
        </div>

        <div className="adg-foot-right">
          <span>Page size:</span>
          <select
            className="adg-size-sel"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
